<template>
    <div class="d-flex flex-column h-100">
        <header v-if="!catalogStore.mobileSearch" class="navbar-expand-lg">
            <SeoButton v-if="user?.can_update_meta"/>
            <Marquee/>
            <nav class="navbar-chart d-print-none">
                <div class="container-xxl">
                    <div class="d-flex justify-content-between align-items-center w-100">
                        <SpotRates/>
                        <div v-if="!isMobile" class="d-none d-lg-flex align-items-center flex-shrink-0 ms-auto">
                            <ColorModePicker />
                            <div class="ps-3">
                                <Preferences />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <nav class="navbar-1">
                <div class="container-xxl pb-2 pb-md-0">
                    <HeaderOptions @toggle-menu="toggleMenu"/>
                </div>
            </nav>

            <nav v-if="!isMobile" class="navbar-2 d-none d-lg-block pb-3">
                <div class="container-xxl px-0">
                    <TopMainMenu/>
                </div>
            </nav>
        </header>

        <div v-if="isMobile" class="position-relative mobile-menu-wrapper">
            <div
                class="offcanvas offcanvas-start d-lg-none"
                id="mobileMenuOffcanvas"
                ref="offcanvasEl"
                tabindex="-1"
                @touchstart="onTouchStart"
                @touchend="onTouchEnd"
                style="width:90%;"
            >
                <div class="offcanvas-body d-flex flex-column p-0">
                    <ClientOnly>
                        <MobileMenu @toggle-dropdown="offcanvasInstance.hide()"/>
                    </ClientOnly>
                </div>
            </div>
            <button
                v-if="isOffcanvasVisible"
                type="button"
                class="btn-close"
                @click="offcanvasInstance.hide()"
                aria-label="Close"
            />
        </div>

        <main class="pb-5 flex-shrink-0">
            <div class="full-height"><slot/></div>
        </main>
        <footer class="bg-footer mt-auto pb-2 pb-xl-4 d-print-none" v-if="!catalogStore.mobileSearch">
            <Footer/>
        </footer>
        <GlobalModals/>
    </div>
</template>

<script setup>
const HeaderOptions = defineAsyncComponent(() => import('~/components/Layout/HeaderOptions.vue'))
const SpotRates = defineAsyncComponent(() => import('~/components/SpotRates.vue'))
const TopMainMenu = defineAsyncComponent(() => import('~/components/Menu/TopMainMenu.vue'))


const Marquee = defineAsyncComponent(() => import('~/components/Layout/Marquee.vue'))
const SeoButton = defineAsyncComponent(() => import('~/components/Layout/SeoButton.vue'))
const Preferences = defineAsyncComponent(() => import('~/components/Layout/Preferences.vue'))
const ColorModePicker = defineAsyncComponent(() => import('~/components/ColorModePicker.vue'))
const MobileMenu = defineAsyncComponent(() => import('~/components/Menu/MobileMenu.vue'))


const Footer = defineAsyncComponent(() => import('~/components/Layout/Footer.vue'))
const GlobalModals = defineAsyncComponent(() => import('~/components/Modals/GlobalModals.vue'))
import { useUserStore } from '~/stores/UserStore'
import { useCatalogStore } from '~/stores/CatalogStore'
import { useNavigationStore } from '~/stores/NavigationStore'
import {useFavicon} from "~/composables/useFavicon";
import { useScrollTo } from '~/composables/useScrollTo';


const isMobile = useMobileDetector();

const user = useUserStore().user
const catalogStore = useCatalogStore()

const touchStartX = ref(0)
let offcanvasInstance;
const offcanvasEl = ref(null);
const isOffcanvasVisible = ref(false);

// Initialize scroll composable
useScrollTo();

onMounted(async () => {
    if (!isMobile) {
        return;
    }
    const { default: Offcanvas } = await import('bootstrap/js/dist/offcanvas')
    offcanvasInstance = new Offcanvas(offcanvasEl.value)
    offcanvasEl.value.addEventListener('show.bs.offcanvas', () => {
        isOffcanvasVisible.value = true;
    });
    offcanvasEl.value.addEventListener('hide.bs.offcanvas', () => {
        isOffcanvasVisible.value = false;
    });

    window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
})

function toggleMenu() {
    offcanvasInstance.toggle()
}

function onTouchStart(e) {
    touchStartX.value = e.touches[0].clientX
}

function onTouchEnd(e) {
    if (e.changedTouches[0].clientX - touchStartX.value < -50) {
        offcanvasInstance.hide()
    }
}

function handleScroll() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const windowHeight = window.innerHeight
    const fullHeight = document.documentElement.scrollHeight
    if ((scrollTop + windowHeight) / fullHeight >= 0.5) {
        useNavigationStore().setScrolledHalf()
        window.removeEventListener('scroll', handleScroll)
    }
}

useFavicon();

</script>

<style scoped>
.nav-link { white-space: normal }
</style>
